import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import API_URL from "../../config/ApiUrl";

// Axios errors carry non-serializable members (AxiosHeaders class instances on
// error.response.headers/error.config.headers, an XMLHttpRequest on
// error.request, etc.). Putting the raw error into Redux trips RTK's
// serializability middleware and pollutes state. Project only the bits any
// caller actually needs.
const toSerializableError = (error) => ({
  status: error?.response?.status ?? null,
  statusText: error?.response?.statusText ?? null,
  message:
    error?.response?.data?.message ??
    error?.response?.data?.title ??
    error?.message ??
    "Request failed",
  data: error?.response?.data ?? null,
});

export const getCustomerDetail = createAsyncThunk(
  "CustomerDetail/get",
  async (customerId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${API_URL.CUSTOMER.SLUG}/${customerId}`);
      return data.data;
    } catch (error) {
      return rejectWithValue(toSerializableError(error));
    }
  }
);

export const addCustomerContact = createAsyncThunk(
  "CustomerContact/add",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.post(API_URL.CUSTOMER.CONTACT.SLUG, payload);
      dispatch(getCustomersContact(payload.customerId));
      return data.data;
    } catch (error) {
      return rejectWithValue(toSerializableError(error));
    }
  }
);

export const updateCustomerContact = createAsyncThunk(
  "CustomerContact/update",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.put(API_URL.CUSTOMER.CONTACT.SLUG, payload);
      dispatch(getCustomersContact(payload.customerId));
      return data.data;
    } catch (error) {
      return rejectWithValue(toSerializableError(error));
    }
  }
);

export const deleteCustomerContact = createAsyncThunk(
  "CustomerContact/delete",
  async ({ contactId, customerId }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.delete(
        `${API_URL.CUSTOMER.CONTACT.SLUG}/${contactId}`
      );
      dispatch(getCustomersContact(customerId));
      return data.data;
    } catch (error) {
      return rejectWithValue(toSerializableError(error));
    }
  }
);

export const getCustomersContact = createAsyncThunk(
  "CustomersContact/get",
  async (customerId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        `${API_URL.CUSTOMER.CONTACT.GET}/${customerId}`
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(toSerializableError(error));
    }
  }
);

export const getCustomersAddress = createAsyncThunk(
  "CustomersAddress/get",
  async (customerId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        `${API_URL.CUSTOMER.ADDRESS.GET}/${customerId}`
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(toSerializableError(error));
    }
  }
);

export const addCustomerAddress = createAsyncThunk(
  "addCustomerAddress/post",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.post(
        `${API_URL.CUSTOMER.ADDRESS.SLUG}`,
        payload
      );
      dispatch(getCustomersAddress(payload.customerId));
      return data.data;
    } catch (error) {
      return rejectWithValue(toSerializableError(error));
    }
  }
);

export const updateCustomerAddress = createAsyncThunk(
  "updateCustomerAddress/put",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.put(
        `${API_URL.CUSTOMER.ADDRESS.SLUG}`,
        payload
      );
      dispatch(getCustomersAddress(payload.customerId));
      return data.data;
    } catch (error) {
      return rejectWithValue(toSerializableError(error));
    }
  }
);

const customerDetailSlice = createSlice({
  name: "customerDetail",
  initialState: {
    customer_detail: null,
    loading: false,
    error: null,
    customer_contact_list: [],
    customer_address_list: [],
  },
  reducers: {
    resetCustomerDetail(state) {
      state.customer_detail = null;
      state.customer_contact_list = [];
      state.customer_address_list = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCustomerDetail.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCustomerDetail.fulfilled, (state, action) => {
        state.customer_detail = action.payload;
        state.loading = false;
      })
      .addCase(getCustomerDetail.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      });
    builder.addCase(getCustomersContact.fulfilled, (state, action) => {
      state.customer_contact_list = action.payload;
    });
    builder.addCase(getCustomersAddress.fulfilled, (state, action) => {
      state.customer_address_list = action.payload;
    });
  },
});

export const { resetCustomerDetail } = customerDetailSlice.actions;
export default customerDetailSlice.reducer;
